import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, User, Calendar, Pill, AlertCircle, Heart } from "lucide-react";

const COMMON_CONDITIONS = [
  "Diabetes",
  "High Blood Pressure",
  "Heart Disease",
  "Asthma",
  "Arthritis",
  "Depression/Anxiety",
  "Thyroid Disorder",
  "Cancer History"
];

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    allergies: "",
    medications: "",
    conditions: [] as string[]
  });

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Redirect if profile is already complete
  useEffect(() => {
    if (user?.patient?.first_name && user?.patient?.first_name !== "New") {
      navigate("/triage");
    }
  }, [user, navigate]);

  const toggleCondition = (condition: string) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.includes(condition)
        ? prev.conditions.filter(c => c !== condition)
        : [...prev.conditions, condition]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    // Basic validation
    if (form.firstName.trim().length < 1 || form.firstName.length > 100) {
      toast({
        title: "Invalid first name",
        description: "First name must be between 1 and 100 characters",
        variant: "destructive",
      });
      return;
    }
    
    if (form.lastName.trim().length < 1 || form.lastName.length > 100) {
      toast({
        title: "Invalid last name",
        description: "Last name must be between 1 and 100 characters",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);

    try {
      // Build risk_flags object with medical history
      const riskFlags = {
        allergies: form.allergies.split(',').map(a => a.trim()).filter(Boolean),
        medications: form.medications.split(',').map(m => m.trim()).filter(Boolean),
        conditions: form.conditions
      };

      // Update the patient record with the new info
      const { error } = await supabase
        .from("patients")
        .update({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          dob: form.dob || null,
          risk_flags: riskFlags
        })
        .eq("user_id", user.id);

      if (error) throw error;

      // Refresh profile to get updated data
      await refreshProfile();

      toast({
        title: "Profile completed!",
        description: "Welcome to CheckFirst",
      });

      navigate("/triage");
    } catch (error: any) {
      console.error("Profile update error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Complete Your Profile - CheckFirst</title>
        <meta name="description" content="Complete your patient profile to access CheckFirst triage services." />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center bg-background p-4 py-8">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Complete Your Profile</h1>
            <p className="text-muted-foreground mt-2">
              Tell us about yourself for personalized care
            </p>
          </div>

          <Card className="health-card">
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>
                This information helps us provide accurate triage recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      value={form.firstName}
                      onChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                      maxLength={100}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      value={form.lastName}
                      onChange={(e) => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                      maxLength={100}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="dob" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date of Birth
                  </Label>
                  <Input
                    id="dob"
                    type="date"
                    value={form.dob}
                    onChange={(e) => setForm(prev => ({ ...prev, dob: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional - helps with age-appropriate recommendations
                  </p>
                </div>

                {/* Medical History Section */}
                <div className="border-t pt-6 space-y-6">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Heart className="w-5 h-5 text-primary" />
                    Medical History
                  </h3>
                  
                  {/* Conditions */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Existing Conditions
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Select any conditions that apply to you
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {COMMON_CONDITIONS.map((condition) => (
                        <Badge
                          key={condition}
                          variant={form.conditions.includes(condition) ? "default" : "outline"}
                          className="cursor-pointer hover:bg-primary/20 transition-colors"
                          onClick={() => toggleCondition(condition)}
                        >
                          {condition}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Allergies */}
                  <div className="space-y-2">
                    <Label htmlFor="allergies" className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Known Allergies
                    </Label>
                    <Textarea
                      id="allergies"
                      placeholder="e.g., Penicillin, Peanuts, Latex (separate with commas)"
                      value={form.allergies}
                      onChange={(e) => setForm(prev => ({ ...prev, allergies: e.target.value }))}
                      maxLength={500}
                      rows={2}
                    />
                    <p className="text-xs text-muted-foreground">
                      Include drug, food, and environmental allergies
                    </p>
                  </div>

                  {/* Medications */}
                  <div className="space-y-2">
                    <Label htmlFor="medications" className="flex items-center gap-2">
                      <Pill className="w-4 h-4" />
                      Current Medications
                    </Label>
                    <Textarea
                      id="medications"
                      placeholder="e.g., Lisinopril 10mg, Metformin 500mg (separate with commas)"
                      value={form.medications}
                      onChange={(e) => setForm(prev => ({ ...prev, medications: e.target.value }))}
                      maxLength={500}
                      rows={2}
                    />
                    <p className="text-xs text-muted-foreground">
                      Include prescription and over-the-counter medications
                    </p>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full mt-6" 
                  disabled={loading || !form.firstName.trim() || !form.lastName.trim()}
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Continue to Triage
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CompleteProfilePage;
